import React, { useState, useMemo } from 'react';
import {
  Box, Container, Typography, Paper, Stack, Button,
  TextField, InputBase, IconButton, Alert,
  Drawer, CircularProgress, Pagination,
} from '@mui/material';
import {
  Add, Delete, Search as SearchIcon,
  Edit, CheckCircle, BookmarkAdd, Close,
} from '@mui/icons-material';
import { usePaths } from '../../context/PathsContext';
import { useCatalog } from '../../context/CatalogContext';
import usePagination from '../../hooks/usePagination';

const PAGE_SIZE = 8;

export default function CreatePathPage() {
  const { paths, create, remove, update, addModule, removeModule } = usePaths();
  const { modules, loadModules, loading } = useCatalog();

  const [showForm, setShowForm]   = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc]   = useState('');
  const [editId, setEditId]       = useState(null);
  const [saved, setSaved]         = useState(false);

  // Module browser
  const [browserOpen, setBrowserOpen] = useState(false);
  const [targetPathId, setTargetPathId] = useState(null);
  const [moduleSearch, setModuleSearch] = useState('');

  const filteredModules = useMemo(() => {
    if (!moduleSearch) return modules;
    const q = moduleSearch.toLowerCase();
    return modules.filter(
      (m) => m.title?.toLowerCase().includes(q) || (m.products || []).some((p) => p.toLowerCase().includes(q))
    );
  }, [modules, moduleSearch]);

  const pagination = usePagination(filteredModules, PAGE_SIZE);

  const openBrowser = (pathId) => {
    setTargetPathId(pathId);
    setBrowserOpen(true);
    if (modules.length === 0) loadModules();
  };

  const handleSave = () => {
    if (!formTitle.trim()) return;
    if (editId) {
      update(editId, { title: formTitle, description: formDesc });
    } else {
      create({ title: formTitle, description: formDesc, modules: [] });
    }
    setFormTitle('');
    setFormDesc('');
    setEditId(null);
    setShowForm(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleEdit = (path) => {
    setEditId(path.id);
    setFormTitle(path.title);
    setFormDesc(path.description || '');
    setShowForm(true);
  };

  const handleAddModule = (module) => {
    if (targetPathId) addModule(targetPathId, { uid: module.uid, title: module.title, type: 'module', duration_in_minutes: module.duration_in_minutes });
  };

  const targetPath = paths.find((p) => p.id === targetPathId);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 4 }} spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={800} letterSpacing="-1px">My Learning Paths</Typography>
          <Typography color="text.secondary">
            {paths.length} custom path{paths.length !== 1 ? 's' : ''} saved in your browser
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<Add />} size="large"
          onClick={() => { setShowForm(true); setEditId(null); setFormTitle(''); setFormDesc(''); }}
          sx={{ borderRadius: '10px', fontWeight: 700, flexShrink: 0 }}
        >
          New path
        </Button>
      </Stack>

      {saved && <Alert severity="success" sx={{ mb: 3 }}>Path saved!</Alert>}

      {/* Create / edit form */}
      {showForm && (
        <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {editId ? 'Edit path' : 'Create new path'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth label="Path title" value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="e.g. Azure Cloud Engineer Journey"
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth multiline rows={2} label="Description (optional)"
              value={formDesc} onChange={(e) => setFormDesc(e.target.value)}
              placeholder="Describe this learning journey..."
              inputProps={{ maxLength: 500 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={!formTitle.trim()}
                sx={{ borderRadius: '8px', fontWeight: 700 }}>
                {editId ? 'Save changes' : 'Create path'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Paths list */}
      {paths.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <BookmarkAdd sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No paths yet</Typography>
          <Typography color="text.disabled" sx={{ mt: 1, mb: 3 }}>
            Create your first learning path to organise modules you want to complete
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setShowForm(true)}
            sx={{ borderRadius: '10px', fontWeight: 700 }}>
            Create first path
          </Button>
        </Box>
      ) : (
        <Stack spacing={3}>
          {paths.map((path) => (
            <Paper key={path.id} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
              {/* Path header */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700}>{path.title}</Typography>
                  {path.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{path.description}</Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    {(path.modules || []).length} module{(path.modules || []).length !== 1 ? 's' : ''} ·
                    Created {new Date(path.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <IconButton size="small" onClick={() => handleEdit(path)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(path.id)}><Delete fontSize="small" /></IconButton>
                </Stack>
              </Stack>

              {/* Modules in path */}
              {(path.modules || []).length > 0 ? (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {path.modules.map((m, i) => (
                    <Box
                      key={m.uid}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.5, borderRadius: '8px', backgroundColor: '#f9fafb',
                        border: '1px solid #f3f4f6',
                      }}
                    >
                      <Box sx={{ width: 24, height: 24, borderRadius: '6px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Typography variant="caption" fontWeight={700} color="primary">{i + 1}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{m.title}</Typography>
                      {m.duration_in_minutes && (
                        <Typography variant="caption" color="text.disabled">{m.duration_in_minutes}m</Typography>
                      )}
                      <IconButton size="small" onClick={() => removeModule(path.id, m.uid)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: '#f9fafb', textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.disabled">No modules added yet</Typography>
                </Box>
              )}

              <Button
                variant="outlined" startIcon={<Add />} size="small"
                onClick={() => openBrowser(path.id)}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Add modules
              </Button>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Module Browser Drawer */}
      <Drawer
        anchor="right"
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Add to: {targetPath?.title}
          </Typography>
          <IconButton onClick={() => setBrowserOpen(false)}><Close /></IconButton>
        </Stack>

        <Paper sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, mb: 2, border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: 'none' }}>
          <SearchIcon sx={{ color: 'text.disabled', mr: 1 }} />
          <InputBase
            fullWidth placeholder="Search modules..." value={moduleSearch}
            onChange={(e) => { setModuleSearch(e.target.value); pagination.reset(); }}
          />
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Stack spacing={1.5} sx={{ flex: 1, overflowY: 'auto' }}>
              {pagination.pageItems.map((m) => {
                const alreadyIn = (targetPath?.modules || []).some((mod) => mod.uid === m.uid);
                return (
                  <Box
                    key={m.uid}
                    sx={{
                      p: 1.5, borderRadius: '10px', border: '1px solid #e5e7eb',
                      display: 'flex', alignItems: 'flex-start', gap: 1.5,
                      opacity: alreadyIn ? 0.6 : 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>{m.title}</Typography>
                      {m.duration_in_minutes && (
                        <Typography variant="caption" color="text.secondary">{m.duration_in_minutes}m</Typography>
                      )}
                    </Box>
                    <Button
                      size="small" variant={alreadyIn ? 'text' : 'outlined'}
                      startIcon={alreadyIn ? <CheckCircle /> : <Add />}
                      disabled={alreadyIn}
                      onClick={() => handleAddModule(m)}
                      sx={{ borderRadius: '8px', flexShrink: 0 }}
                    >
                      {alreadyIn ? 'Added' : 'Add'}
                    </Button>
                  </Box>
                );
              })}
            </Stack>
            {pagination.totalPages > 1 && (
              <Box sx={{ pt: 2 }}>
                <Pagination count={pagination.totalPages} page={pagination.page}
                  onChange={(_, p) => pagination.goTo(p)} size="small" />
              </Box>
            )}
          </>
        )}
      </Drawer>
    </Container>
  );
}
